<template>
  <b-card>

    <validation-observer ref="simpleRules">
      <b-form>
        <!-- Section Informations -->
        <h4>{{ $t('Informations') }}</h4>
        <div class="d-flex justify-content-between align-items-center">
          <span class="font-weight-bold">{{ $t('The fields with (*) are required') }}</span>
          <b-button
              v-if="canViewHistory && userData.id"
              v-b-tooltip.hover
              :title="$t('View History')"
              variant="flat-secondary"
              size="sm"
              class="btn-icon btn-history-minimal"
              @click="openHistoryModal"
          >
            <feather-icon icon="ClockIcon" size="14"/>
          </b-button>
        </div>
        <hr class="mb-2">
        <b-row v-if="user.role === 'SYSADMIN' && userData.id" class="d-flex align-items-center mb-2">
          <b-col md="6">
            <label for="whatsapp">{{ $t('WhatsApp Number') }}*</label>
            <div class="input-group">
              <input id="whatsapp" type="text" class="form-control" v-model="userData.whatsAppNumber" :placeholder="$t('WhatsApp number example')">
              <div class="input-group-append">
                <button class="btn btn-success" @click="setWhatsApp" type="button">{{ $t('Save WhatsApp') }}</button>
              </div>
            </div>
          </b-col>
        </b-row>
        
        <b-row>

          <!-- Field: Language -->
          <b-col cols="12" md="2" style="list-style: none;">
            <b-form-group :label="$t('Language') + '*'" label-for="language">
              <validation-provider #default="{ errors }" name="profile" rules="required">
                <v-select :state="errors.length > 0 ? false : null" v-model="userData.language"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="languageOptions"
                          :reduce="val => val.value" :clearable="false" input-id="profile"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>

            </b-form-group>
          </b-col>

          <!-- Field: Name -->
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Name') + '*'" label-for="name">
              <validation-provider #default="{ errors }" name="name" rules="required|min:3|max:50">
                <b-form-input id="name" v-model="userData.name"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Field:  Email -->
          <b-col cols="12" md="6">
            <b-form-group :label="$t('Email') + '*'" label-for="email">
              <validation-provider #default="{ errors }" name="email" rules="required|email|max:255">
                <b-form-input id="email" v-model="userData.email" @input="convertToLowerCase"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Field: Customer -->
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Customer')" label-for="customer">
              <vue-autosuggest v-if="!userData.id || (userData.id && user.role === 'TRCKOWN') " v-model="searchQuery" :suggestions="filteredOptions"
                               :input-props="customerInputProps" @selected="selectHandler" @input="onInputChange">
                <template slot-scope="{suggestion}">
                  <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                  <div v-show="suggestion.item.ownerName">
                    <span class="text-muted">{{ $t('Belongs to: ') }}</span>
                    <span class="font-weight-bold"> {{ suggestion.item.ownerName }} </span>
                  </div>
                </template>
              </vue-autosuggest>

              <b-form-input v-if="userData.id && user.role !== 'TRCKOWN'" id="customer" v-model="searchQuery" disabled/>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4">
            <b-form-group :label="$t('Profile')+'*'" label-for="profile">
              <validation-provider #default="{ errors }" name="profile" rules="required">
                <v-select :state="errors.length > 0 ? false : null" v-model="userData.profileId"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="profileOptions"
                          :reduce="val => val.value" :clearable="false" input-id="profile"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12" class="mb-2">
            <user-profile-guide />
          </b-col>

          <!-- Field: Password -->
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Password') + '*'" label-for="password">
              <validation-provider #default="{ errors }" name="password" :rules="rulesPass()">
                <b-form-input id="password" v-model="userData.password" type="password"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Field:  Timezone -->
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Timezone') + '*'" label-for="time">
              <validation-provider #default="{ errors }" name="time" rules="required">
                <b-form-input id="time" v-model="userData.timezone"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Field: Bloked -->
          <b-col cols="12" md="4">
            <b-form-group :label="$t('Blocked') + '*'" label-for="bloked">
              <validation-provider #default="{ errors }" name="blocked" rules="required">
                <v-select v-model="userData.blocked" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :options="blokedOptions" :reduce="val => val.value" :clearable="false"
                          input-id="bloked"/>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-button variant="primary" type="submit" @click.prevent="validationForm">
              {{ $t('Save') }}
            </b-button>
          </b-col>
        </b-row>

      </b-form>
    </validation-observer>

    <user-history-modal ref="historyModalRef"/>
  </b-card>
</template>

<script>
import {
  BTab,
  BTabs,
  BCard,
  BAlert,
  BLink,
} from 'bootstrap-vue'
import {
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BCardText,
  BFormTextarea,
  BNavItemDropdown,
  BDropdownItem,
  BImg,
} from 'bootstrap-vue'
import {
  ref,
  computed,
  onUnmounted
} from '@vue/composition-api'
import i18n from '@/libs/i18n'
import router from '@/router'
import store from '@/store'
import userStoreModule from './userStoreModule'
import UserHistoryModal from './components/UserHistoryModal.vue'
import { canViewUserHistory } from './useUserHistory'

import Cleave from 'vue-cleave-component'
import 'cleave.js/dist/addons/cleave-phone.br'

import vSelect from 'vue-select'
import {
  VueAutosuggest
} from 'vue-autosuggest'

import {
  ValidationProvider,
  ValidationObserver
} from 'vee-validate'
import {
  required,
  email,
  confirmed,
  url,
  between,
  alpha,
  integer,
  password,
  min,
  max,
  digits,
  alphaDash,
  length,
} from '@validations'

import UserProfileGuide from '@/views/main/user/components/UserProfileGuide.vue'
import { mapProfilesToSelectOptions } from './profileHierarchy'

export default {
  components: {
    BTab,
    BTabs,
    BCard,
    BAlert,
    BLink,

    ValidationProvider,
    ValidationObserver,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BCardText,
    BFormTextarea,
    BNavItemDropdown,
    BDropdownItem,
    BImg,

    Cleave,
    vSelect,
    VueAutosuggest,
    UserHistoryModal,
    UserProfileGuide

  },
  data() {
    return {
      myId: router.currentRoute.params.id,
      required,
      email,
      confirmed,
      url,
      between,
      alpha,
      integer,
      password,
      min,
      digits,
      alphaDash,
      length,
      max,

     
      options: {

        cpf: {
          delimiters: ['.', '.', '-'],
          blocks: [3, 3, 3, 2],
          uppercase: true,
        },
        cnpj: {
          delimiters: ['.', '.', '/', '-'],
          blocks: [2, 3, 3, 4, 2],
          uppercase: true,

        },

      },

      customerOptions: [{
        label: 'Infofleet',
        value: 1
      },
        {
          label: 'Bamaq',
          value: 2
        },
        {
          label: 'Aguarda',
          value: 3
        },
      ],

      datasuggest: [],
      filteredOptions: [],
      limit: 3,
      selected: null,
    }
  },
  computed: {
    currentLocale() {
      return this.locales.find(l => l.locale === this.$i18n.locale)
    },
    blokedOptions() {
      return [
        { label: this.$t('Yes'), value: true },
        { label: this.$t('No'), value: false },
      ]
    },
    languageOptions() {
      return [
        { label: this.$t('Portuguese'), value: 'pt' },
        { label: this.$t('English'), value: 'en' },
        { label: this.$t('Spanish'), value: 'es' },
      ]
    },
    customerInputProps() {
      return {
        id: 'autosuggest__input',
        class: 'form-control',
        placeholder: this.$t('Search for your customer ...'),
      }
    },
  },
  methods: {
    setWhatsApp(){
      if (!this.isValidWhatsAppNumber(this.userData.whatsAppNumber)) {
        this.msgDialog(this.$t('Invalid WhatsApp number! Use the format: +553599290470'), 'error')
        return;
      }
      store.dispatch('app-user/setWhatsApp', {
        userId: this.userData.id,
        whatsapp: this.userData.whatsAppNumber
      })
          .then(response => {
            this.msgDialog(this.$t('Success'), 'success')
          })
          .catch(error => {
            this.msgDialog(this.$t('Failed'), 'error')
          })
    },
    isValidWhatsAppNumber(number) {
      const regex = /^\+\d{12,13}$/;
      return regex.test(number);
    },

    convertToLowerCase(event) {
      this.userData.email = event.toLowerCase();
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          //this.userData.language = this.$i18n.locale
          if (this.userData.id === "") {
            this.userData.email = this.userData.email.toLowerCase()
            store.dispatch('app-user/addUser', {
              userData: this.userData
            })
                .then(response => {
                  //alert('Error 404')
                  this.msgDialog(this.$t('Successful registration'), 'success')
                  router.push({
                    name: 'list-user'
                  })

                })
                .catch(error => {
                  this.msgDialog(this.$t('Failed to save'), 'error')
                })
          } else {
            if (!this.userData.password) this.userData.password = ""
            store.dispatch('app-user/updateUser', {

              userData: this.userData
            })
                .then(response => {
                  //alert('Error 404')
                  this.msgDialog(this.$t('Successful update'), 'success')
                  this.userData.password = ``
                  //this.clearForm();

                })
                .catch(error => {
                  this.msgDialog(this.$t('FAILURE to update'), 'error')

                })
          }
        }
      })
    },

    rulesPass() {
      if (this.userData.id === "") return 'required|min:4|max:16'
      return ''
    },

    clearForm() {
      this.userData = {
        id: '',
        name: '',
        email: '',
        user: '',
        password: '',
        customerId: '',
        language: 'pt',
        blocked: false
      }
    },

    msgDialog(msg, icon) {
      this.$swal({
        position: 'top-end',
        icon: icon,
        title: msg,
        showConfirmButton: false,
        timer: 1500 * 3,
        customClass: {
          confirmButton: 'btn btn-primary',
        },

        buttonsStyling: false,
      })
    },

    selectHandler(option) {
      this.selected = option.item
      this.filteredOptions = []
      this.searchQuery = option.item.name
      this.userData.customerId = option.item.id

    },

    onInputChange(text) {
      if (text === '' || text === undefined || text.length < 3) {

        return
      }

      store.dispatch('app-user/fetchCustomers', {
        q: this.searchQuery,

      })
          .then(response => {

            this.filteredOptions = [{
              data: response.data.data,
            }]

          })
          .catch(() => {})
    },
    suggestionSelected() {
    },

    openHistoryModal() {
      if (!this.userData?.id || !this.canViewHistory) return

      this.$refs.historyModalRef.open({
        id: this.userData.id,
        name: this.userData.name,
        email: this.userData.email,
      })
    },
  },
  setup() {
    const userData = ref(null)
    const searchQuery = ref(``)
    const historyModalRef = ref(null)

    const USER_APP_STORE_MODULE_NAME = 'app-user'
    const user = localStorage.userData != undefined ? JSON.parse(localStorage.userData) : {}

    // Register module
    if (!store.hasModule(USER_APP_STORE_MODULE_NAME)) store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(USER_APP_STORE_MODULE_NAME)) store.unregisterModule(USER_APP_STORE_MODULE_NAME)
    })

    const locales = computed(() => [{
      locale: 'en',
      img: require('@/assets/images/flags/en.png'),
      name: i18n.t('English'),
    },
      {
        locale: 'pt',
        img: require('@/assets/images/flags/br.png'),
        name: i18n.t('Portuguese'),
      },
      {
        locale: 'es',
        img: require('@/assets/images/flags/ar.png'),
        name: i18n.t('Spanish'),
      },
    ])

    const profileOptions = ref([])

    if (router.currentRoute.params.id != undefined) {

      store.dispatch('app-user/fetchUser', {
        //id: router.currentRoute.params.id
        id: router.currentRoute.params.id
      })
          .then(response => {

            userData.value = response.data

            store.dispatch('app-user/fetchCustomer', {
              //id: router.currentRoute.params.id
              id: response.data.customerId
            })
                .then(res => {
                  searchQuery.value = res.data.name;

                })
                .catch(() => {})
          })
          .catch(() => {})
    } else {
      userData.value = {
        id: '',
        name: '',
        email: '',
        user: '',
        password: '',
        customerId: null,
        blocked: false,
        language: `pt`,
        timezone: `-3`,
        profileId: '',
        whatsAppNumber:''
      }

    }

    store.dispatch('app-user/fetchProfiles', {
      id: router.currentRoute.params.id
    })
        .then(response => {
          const profiles = response.data.data || []
          if (profiles.length > 0) {
            profileOptions.value = mapProfilesToSelectOptions(profiles)
          }
        })
        .catch(() => {})

    const canViewHistory = computed(() => canViewUserHistory(user))

    return {
      userData,
      searchQuery,
      locales,
      profileOptions,
      user,
      historyModalRef,
      canViewHistory,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
</style>
<style>
.title-head {
  margin-bottom: 32px;
}

.btn-history-minimal {
  width: 28px;
  height: 28px;
  padding: 0;
  flex-shrink: 0;
}
</style>
